/**
 * String casing and humanizing helpers — one body per name.
 *
 * Every surface that turns a machine key, a URL segment or a raw label into display text
 * imports from here. The four helpers below are deliberately distinct rules, not variants
 * of one rule, and each doc comment says which inputs it is for; adding a fifth private
 * copy in a feature file is the thing this module exists to stop.
 */
/**
 * Split a machine key into space-separated words: camelCase boundaries, `_`, `-` and `.`
 * all become single spaces.
 *
 * The primitive under `humanizeKey` and the entity-type label helpers. It deliberately
 * leaves casing alone, because its callers disagree about casing on purpose — one wants
 * "Decision Objective", another wants "decision objective" for mid-sentence use.
 */
export function splitKeyWords(value) {
    return value
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_.-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/**
 * Uppercase the first letter of every space-separated word and leave the rest of each word
 * exactly as it was — so `"related KPIs"` stays `"Related KPIs"`.
 *
 * Use this when the input's inner casing is already meaningful. When it is not (a free-form
 * user label that may arrive SHOUTING), use `titleCaseWords`.
 */
export function capitalizeWords(value) {
    return value
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(" ");
}
/**
 * Title-case a free-form label: all-caps words are kept as acronyms (`KPI`), every other
 * word is capitalized and its remainder lowercased (`dECISION` → `Decision`).
 *
 * Operates on letter runs rather than whitespace, so punctuation and apostrophes survive.
 */
export function titleCaseWords(value) {
    return value.replace(/[A-Za-z]+(?:'[A-Za-z]+)?/g, (word) => /^[A-Z0-9]+$/.test(word)
        ? word
        : `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`);
}
/**
 * Title-case one URL path segment for a breadcrumb or link label: percent-decoded,
 * `-`/`_` turned into spaces, each word capitalized with its remainder untouched.
 *
 * Returns the original value when the segment has no letters left to capitalize.
 */
export function titleCaseSegment(value) {
    const normalized = decodeURIComponent(value).replace(/[-_]+/g, " ").trim();
    if (!normalized)
        return value;
    return capitalizeWords(normalized);
}
/**
 * Turn a machine key into display text: `"decisionObjective"` → `"Decision Objective"`,
 * `"platform_default"` → `"Platform Default"`, `"entity.overview"` → `"Entity Overview"`.
 *
 * This is the display fallback for entity type keys, tab keys, field ids and enum values —
 * anywhere a compiled label is missing and the key itself has to be shown to a person.
 */
export function humanizeKey(value) {
    return capitalizeWords(splitKeyWords(value)) || value;
}
